/**
 * Escada de AOV — <cart-goals>
 * Segue o padrão do FreeShippingGoal (cart.js): escuta `cart:updated` e atualiza
 * barra, pins e label. Além disso sincroniza brindes com auto-add: cruzou o marco,
 * adiciona a variante com property `_brinde`; caiu abaixo, remove.
 * A gratuidade do brinde vem de um desconto automático "Compre X ganhe Y" no admin.
 */
(() => {
  const GIFT_PROPERTY = '_brinde';
  let busy = false;
  const failedVariants = new Set();

  class CartGoals extends HTMLElement {
    constructor() {
      super();
      const rate = Number(window.Shopify?.currency?.rate || 1);
      try {
        this.goals = JSON.parse(this.dataset.goals || '[]');
      } catch (e) {
        this.goals = [];
      }
      this.goals = this.goals
        .map((goal) => ({ ...goal, amount: goal.amount * rate }))
        .sort((a, b) => a.amount - b.amount);

      this.label = this.querySelector('[data-goals-label]');
      this.progress = this.querySelector('progress-bar');
      this.pins = Array.from(this.querySelectorAll('.cart-goals__pin')).sort(
        (a, b) => Number(a.dataset.goalAmount) - Number(b.dataset.goalAmount)
      );
      this.moneyFormat = window.FoxTheme?.settings?.moneyFormat;
      this.textRemaining = this.dataset.textRemaining || '';
      this.textComplete = this.dataset.textComplete || '';
      this.textIncluded = this.dataset.textIncluded || '';
      try {
        this.eligibleIds = JSON.parse(this.dataset.eligibleIds || '[]');
      } catch (e) {
        this.eligibleIds = [];
      }
    }

    connectedCallback() {
      if (!this.goals.length) {
        this.classList.add('hidden');
        return;
      }

      this.positionPins();
      // estado inicial server-side: subtotais + flag `included` já vem no JSON dos marcos
      this.paint(
        this.states(
          {
            eligible: Number(this.dataset.subtotal || 0),
            full: Number(this.dataset.subtotalFull || 0),
          },
          null
        )
      );

      this.onCartUpdated = (event) => {
        const cart = event.detail?.cart;
        if (!cart || !Array.isArray(cart.items)) return;
        const states = this.states(this.subtotals(cart), cart);
        this.paint(states);
        this.syncGifts(cart, states);
      };
      document.addEventListener('cart:updated', this.onCartUpdated);

      // Estado inicial pode estar defasado (ex.: item removido na página do carrinho
      // antes do desconto existir) — busca o cart real uma vez e sincroniza.
      fetch(`${FoxTheme.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => {
          const states = this.states(this.subtotals(cart), cart);
          this.paint(states);
          this.syncGifts(cart, states);
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    /* full = tudo menos brindes (régua do frete, espelha o checkout);
       eligible = filtrado pela coleção (régua dos brindes, espelha o desconto) */
    subtotals(cart) {
      return cart.items.reduce(
        (subs, item) => {
          if (item.properties && item.properties[GIFT_PROPERTY]) return subs;
          subs.full += item.original_line_price;
          if (!this.eligibleIds.length || this.eligibleIds.includes(item.product_id)) {
            subs.eligible += item.original_line_price;
          }
          return subs;
        },
        { full: 0, eligible: 0 }
      );
    }

    metricFor(goal, subs) {
      return goal.type === 'shipping' ? subs.full : subs.eligible;
    }

    /* Brinde "já incluído": algum produto da lista do marco (ex.: kit que já vem
       com o brinde) está no carrinho. Conta como conquistado, mas sem auto-add. */
    includedIn(goal, cart) {
      if (!cart) return Boolean(goal.included);
      const ids = goal.includedIds || [];
      if (!ids.length) return false;
      return cart.items.some(
        (item) => !(item.properties && item.properties[GIFT_PROPERTY]) && ids.includes(item.product_id)
      );
    }

    /* Um estado por marco, na ordem dos goals. cart=null usa a flag inicial do JSON. */
    states(subs, cart) {
      return this.goals.map((goal) => {
        const metric = this.metricFor(goal, subs);
        const byAmount = metric >= goal.amount;
        const included = goal.type === 'gift' && this.includedIn(goal, cart);
        return { goal, metric, byAmount, included, reached: byAmount || included };
      });
    }

    positionPins() {
      const count = this.pins.length;
      this.pins.forEach((pin, index) => {
        pin.style.setProperty('--pos', `${((index + 1) / count) * 100}%`);
      });
    }

    /* Progresso por segmentos iguais (estilo Hidratei): cada marco ocupa a mesma
       largura visual, o valor interpola dentro do segmento usando a régua do marco. */
    percentFor(states) {
      const count = states.length;
      let previous = 0;
      for (let i = 0; i < count; i++) {
        const { goal, metric, reached } = states[i];
        if (!reached) {
          const fraction = (metric - previous) / (goal.amount - previous);
          return ((i + Math.max(0, Math.min(1, fraction))) / count) * 100;
        }
        previous = goal.amount;
      }
      return 100;
    }

    paint(states) {
      const empty = !states.some((state) => state.metric > 0 || state.included);
      this.classList.toggle('hidden', empty);
      if (empty) return;

      this.progress.style.setProperty('--percent', `${this.percentFor(states)}%`);

      // pins e goals estão ambos ordenados por valor — índice casa com índice
      this.pins.forEach((pin, index) => {
        const state = states[index];
        if (!state) return;
        pin.classList.toggle('is-reached', state.reached);
      });

      const next = states.find((state) => !state.reached);
      if (!next) {
        // tudo conquistado: se algum brinde veio "de dentro de um kit", diz isso
        const includedOnly = states.filter((state) => state.included && !state.byAmount);
        this.label.innerHTML = includedOnly.length
          ? this.textIncluded.replace('[goal]', includedOnly.map((state) => state.goal.title).join(', '))
          : this.textComplete;
        this.classList.add('cart-goals--done');
        return;
      }
      this.classList.remove('cart-goals--done');
      const remaining = FoxTheme.Currency.formatMoney(next.goal.amount - next.metric, this.moneyFormat);
      this.label.innerHTML = this.textRemaining
        .replace('[amount]', `<strong>${remaining}</strong>`)
        .replace('[goal]', next.goal.title);
    }

    async syncGifts(cart, states) {
      if (busy) return;

      for (const { goal, byAmount, included } of states) {
        if (goal.type !== 'gift' || !goal.autoAdd || !goal.variantId) continue;
        if (failedVariants.has(goal.variantId)) continue;

        const line = cart.items.find(
          (item) => item.variant_id === goal.variantId && item.properties && item.properties[GIFT_PROPERTY]
        );

        // brinde já vem dentro de um kit no carrinho: não adiciona um segundo
        if (byAmount && !included && !line) {
          await this.mutate(goal, 'add');
          return; // cart:updated re-dispara e reavalia os demais marcos
        }
        if (!byAmount && line) {
          await this.mutate(goal, 'remove', line);
          return;
        }
      }
    }

    async mutate(goal, action, line) {
      busy = true;
      try {
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        let response;
        if (action === 'add') {
          response = await fetch(FoxTheme.routes.cart_add_url, {
            ...FoxTheme.utils.fetchConfig(),
            body: JSON.stringify({
              items: [{ id: goal.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }],
              sections: sectionsToBundle,
            }),
          });
        } else {
          response = await fetch(FoxTheme.routes.cart_change_url, {
            ...FoxTheme.utils.fetchConfig(),
            body: JSON.stringify({ id: line.key, quantity: 0, sections: sectionsToBundle }),
          });
        }

        const parsedState = await response.json();
        if (!response.ok || parsedState.status) {
          failedVariants.add(goal.variantId);
          console.warn('[cart-goals] falha ao sincronizar brinde:', parsedState.description || parsedState.message);
          return;
        }

        // add.js não devolve o cart completo — busca e anexa as sections renderizadas
        // pra reaproveitar o fluxo de re-render do tema (CartItems.onCartUpdate).
        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
        cartJson.sections = parsedState.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      } catch (error) {
        console.warn('[cart-goals] erro de rede ao sincronizar brinde', error);
      } finally {
        busy = false;
      }
    }
  }

  if (!customElements.get('cart-goals')) {
    customElements.define('cart-goals', CartGoals);
  }

  /**
   * Conversão em kit OPT-IN: se os componentes avulsos de um kit estão no
   * carrinho, mostra oferta "Leve o kit e economize R$ X" com botão.
   * Só troca quando a cliente clica. Ordem segura: adiciona o kit primeiro,
   * depois reduz os componentes.
   */
  class CartKitConverter extends HTMLElement {
    constructor() {
      super();
      try {
        this.kits = JSON.parse(this.dataset.kits || '[]');
      } catch (e) {
        console.warn('[cart-kit-converter] data-kits inválido', e);
        this.kits = [];
      }
      // trio antes dos pares: combinação maior tem prioridade
      this.kits.sort((a, b) => b.components.length - a.components.length);
      this.savingsLabel = this.dataset.savingsLabel || 'Economia';
      this.textOffer = this.dataset.textOffer || '';
      this.textButton = this.dataset.textButton || '';
      this.moneyFormat = window.FoxTheme?.settings?.moneyFormat;
    }

    connectedCallback() {
      if (!this.kits.length) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-kit-variant]');
        if (button) this.executeSwap(Number(button.dataset.kitVariant));
      });

      this.onCartUpdated = (event) => {
        const cart = event.detail?.cart;
        if (cart && Array.isArray(cart.items)) this.renderOffers(cart);
      };
      document.addEventListener('cart:updated', this.onCartUpdated);

      fetch(`${FoxTheme.routes.cart_url}.js`)
        .then((response) => response.json())
        .then((cart) => this.renderOffers(cart))
        .catch(() => {});
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this.onCartUpdated);
    }

    computePlans(cart) {
      const counts = {};
      cart.items.forEach((item) => {
        if (item.properties && item.properties[GIFT_PROPERTY]) return;
        counts[item.variant_id] = (counts[item.variant_id] || 0) + item.quantity;
      });

      const plans = [];
      for (const kit of this.kits) {
        const n = Math.min(...kit.components.map((c) => counts[c.variantId] || 0));
        if (!n || !isFinite(n)) continue;
        kit.components.forEach((c) => (counts[c.variantId] -= n));
        plans.push({ kit, n });
      }
      return { plans, remaining: counts };
    }

    savingsFor(plan) {
      const separateTotal = plan.kit.components.reduce((sum, c) => sum + c.price, 0);
      return (separateTotal - plan.kit.kitPrice) * plan.n;
    }

    renderOffers(cart) {
      const { plans } = this.computePlans(cart);
      const offers = plans.filter((plan) => this.savingsFor(plan) > 0);

      if (!offers.length) {
        this.classList.add('hidden');
        this.innerHTML = '';
        return;
      }

      this.innerHTML = offers
        .map((plan) => {
          const amount = FoxTheme.Currency.formatMoney(this.savingsFor(plan), this.moneyFormat);
          const text = this.textOffer
            .replace('[kit]', `<strong>${plan.kit.kitTitle}</strong>`)
            .replace('[amount]', `<strong>${amount}</strong>`);
          return `
            <div class="cart-kit-offer">
              <span class="cart-kit-offer__text">${text}</span>
              <button type="button" class="btn btn--primary cart-kit-offer__btn" data-kit-variant="${plan.kit.kitVariantId}">
                ${this.textButton}
              </button>
            </div>`;
        })
        .join('');
      this.classList.remove('hidden');
    }

    async executeSwap(kitVariantId) {
      if (busy) return;
      busy = true;
      try {
        // carrinho fresco na hora do clique — evita agir sobre estado velho
        const cart = await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
        const { plans, remaining } = this.computePlans(cart);
        const plan = plans.find((p) => p.kit.kitVariantId === kitVariantId);
        if (!plan) {
          busy = false;
          this.renderOffers(cart);
          return;
        }

        // 1) adiciona o kit; falhou (esgotou) = aborta sem tocar no carrinho
        // (sem line property: a economia já foi comunicada no banner da oferta)
        const addResponse = await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ items: [{ id: plan.kit.kitVariantId, quantity: plan.n }] }),
        });
        const addState = await addResponse.json();
        if (!addResponse.ok || addState.status) {
          console.warn('[cart-kit-converter] kit indisponível:', addState.description || addState.message);
          return;
        }

        // 2) reduz os componentes pro que sobrou fora do kit
        const updates = {};
        plan.kit.components.forEach((c) => (updates[c.variantId] = remaining[c.variantId]));

        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        const updateResponse = await fetch(FoxTheme.routes.cart_update_url, {
          ...FoxTheme.utils.fetchConfig(),
          body: JSON.stringify({ updates, sections: sectionsToBundle }),
        });
        const parsedState = await updateResponse.json();
        if (!updateResponse.ok) {
          console.warn('[cart-kit-converter] falha ao reduzir componentes; confira o carrinho');
        }

        const cartJson = await (await fetch(`${FoxTheme.routes.cart_url}.js`)).json();
        cartJson.sections = parsedState.sections;
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
      } catch (error) {
        console.warn('[cart-kit-converter] erro de rede na troca', error);
      } finally {
        busy = false;
      }
    }
  }

  if (!customElements.get('cart-kit-converter')) {
    customElements.define('cart-kit-converter', CartKitConverter);
  }
})();
